import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { currentUserAtom, messagesAtom } from '../store';
import { api } from '../lib/api';
import {
  isExpired,
  WS_EVENTS,
  type WsMessageDeletedPayload,
  type WsGatheringPayload,
  type WsPollPayload,
} from '@twinden/shared';
import { hideExpired, MAX_TIMER_MS, nextExpiry } from '../lib/disappearing';
import { applyPollEvent } from '../lib/polls';
import { applyGatheringEvent } from '../lib/gatherings';
import { useWebSocket } from './useWebSocket';
import {
  UNREAD_PAGE_SIZE,
  UNREAD_SEARCH_LIMIT,
  UNREAD_SEARCH_TIMEOUT_MS,
  applyRead,
  firstUnreadMayBeOlder,
  isGroupInfoLine,
  mergeNewest,
  unreadMarker,
  type ChatMessage,
  type UnreadMarker,
} from '../components/chat/messageUtils';

interface MessagePage {
  conversationId: string | null;
  messages: ChatMessage[];
  hasMore: boolean;
  // Server cursor for the next-older page (createdAt of the oldest loaded).
  cursor: string | undefined;
  // The window stops short of the newest message: a jump far into history
  // (D271). Live arrivals wait for "Jump to latest" rather than leave a gap.
  hasNewer?: boolean;
  // Live arrivals already in the store when the window went back, so the
  // button counts only what arrived since (D271).
  newerBaseline?: ReadonlySet<string>;
}

// What the server returns for a message this user just sent: the full
// message payload (M20 §3), though older callers only rely on these fields.
export type SentMessage = Pick<
  ChatMessage,
  'id' | 'conversationId' | 'content' | 'type' | 'metadata' | 'createdAt'
> &
  Partial<ChatMessage>;

// A wake's catch-up that failed tries once more after this (issue #75).
export const CATCH_UP_RETRY_MS = 2000;

const EMPTY: MessagePage = {
  conversationId: null,
  messages: [],
  hasMore: false,
  cursor: undefined,
};

// Messages for the open conversation: the newest page on open, older pages on
// demand, live WebSocket arrivals merged in, and read receipts posted as
// messages come into view.
export function useConversationMessages(
  conversationId: string | null,
  onMarkedRead: (conversationId: string) => void
) {
  const currentUser = useAtomValue(currentUserAtom);
  const wsMessages = useAtomValue(messagesAtom);
  const { subscribe, onEvent, connectionEpoch, onWake } = useWebSocket();
  const [page, setPage] = useState<MessagePage>(EMPTY);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // An older page failed: Try again at the top, no retry loop (D270, D249).
  const [olderError, setOlderError] = useState<{
    conversationId: string;
    error: unknown;
  } | null>(null);
  // The first page's request, for the loader and Try again (M66, D248).
  const [firstPage, setFirstPage] = useState<{
    conversationId: string | null;
    loading: boolean;
    error: unknown;
  }>({ conversationId: null, loading: false, error: null });

  // State is tagged with its conversation so a switch never shows (or scrolls
  // against) the previous chat's messages for a render.
  const current = page.conversationId === conversationId ? page : EMPTY;
  // Disappearing messages go at their time, before the server's sweep
  // (D238).
  // `expiryTick` makes the list look at the clock again: when the timer
  // fires (even early, at setTimeout's ~24.8-day cap, so it re-arms), and
  // when the app wakes, since timers stop while a computer sleeps.
  const [expiryTick, setExpiryTick] = useState(0);
  const pruneExpired = useCallback(() => {
    setExpiryTick((tick) => tick + 1);
    setPage((previous) => {
      const kept = hideExpired(previous.messages);
      return kept === previous.messages
        ? previous
        : { ...previous, messages: kept };
    });
  }, []);
  const messages = useMemo(
    () => hideExpired(current.messages),
    // expiryTick: the same messages, a later clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current.messages, expiryTick]
  );
  useEffect(() => {
    const next = nextExpiry(current.messages);
    if (next === null) return;
    const timer = setTimeout(
      pruneExpired,
      Math.min(Math.max(0, next - Date.now()) + 50, MAX_TIMER_MS)
    );
    return () => clearTimeout(timer);
  }, [current.messages, expiryTick, pruneExpired]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') pruneExpired();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', pruneExpired);
    window.addEventListener('online', pruneExpired);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', pruneExpired);
      window.removeEventListener('online', pruneExpired);
    };
  }, [pruneExpired]);

  const setMessages = useCallback(
    (update: (previous: ChatMessage[]) => ChatMessage[]) => {
      setPage((previous) => ({
        ...previous,
        messages: update(previous.messages),
      }));
    },
    []
  );

  // Load the newest page when the conversation changes, or on request (a
  // chat key arrived, M28).
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken((token) => token + 1), []);
  // The "N unread messages" divider (M62, D229): placed once per open, from
  // the page as it arrived, before anything was marked read.
  const [unread, setUnread] = useState<{
    conversationId: string;
    marker: UnreadMarker | null;
    /** Newest message when the chat opened: my sends after it clear it. */
    openedAt: string;
  } | null>(null);
  const loadingOlderRef = useRef(false);
  // While the background search runs, nothing is marked read (D229): marking
  // moves my watermark to the newest message, so the older pages it fetches
  // after that would come back read and the divider would land too low.
  const [searchingUnread, setSearchingUnread] = useState(false);
  // Ids already posted as read; the search's cleanup shares it (below).
  const markedRef = useRef<Set<string>>(new Set());
  const onMarkedReadRef = useRef(onMarkedRead);
  onMarkedReadRef.current = onMarkedRead;
  const pageRef = useRef(page);
  pageRef.current = page;
  const unreadRef = useRef(unread);
  unreadRef.current = unread;
  const userId = currentUser?.id;
  const wsMessagesRef = useRef(wsMessages);
  wsMessagesRef.current = wsMessages;
  // Which window is on screen: bumped whenever one replaces another (the
  // first page, a jump, the newest page, a send from far back). A request
  // started for an earlier window drops its answer (D270–D271), so an older
  // page or the unread search never lands on a window it wasn't asked for.
  const windowGenRef = useRef(0);
  const newWindow = useCallback(() => {
    windowGenRef.current += 1;
    setOlderError(null);
  }, []);
  // Leaving the chat drops it: next time those messages are read.
  useEffect(() => {
    setUnread(null);
    unreadRef.current = null;
    // A failed older page belongs to that visit (D270).
    setOlderError(null);
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId) return;

    let cancelled = false;
    // What the unread search has loaded, while it runs, so a switch mid-search
    // can still mark this chat read.
    let searching: ChatMessage[] | null = null;
    setFirstPage({ conversationId, loading: true, error: null });
    const load = async () => {
      const response = await api.getMessages(conversationId);
      if (cancelled) return;
      if (!response.success) throw new Error('Could not load messages');
      setFirstPage({ conversationId, loading: false, error: null });
      const fresh = (response.data as ChatMessage[]).reverse();
      let hasMore: boolean = response.pagination.hasMore;
      let cursor: string | undefined = response.pagination.cursor;
      // Reloads of the same chat (keys arrived) keep the first divider.
      const opening = unreadRef.current?.conversationId !== conversationId;
      const shown = pageRef.current;
      if (
        !opening &&
        shown.conversationId === conversationId &&
        shown.messages[0] &&
        (shown.hasNewer || shown.messages.length > fresh.length)
      ) {
        // Keys arrived with older pages on screen: open the whole window
        // again in one request rather than dropping back to the first page.
        const window = await api
          .getMessagesAfter(conversationId, shown.messages[0].createdAt)
          .catch(() => null);
        if (cancelled) return;
        if (shown.hasNewer) {
          // Reading far back (D271): fetch the same rows again, page by
          // page from the oldest held to the newest held, and swap each held
          // row for its fresh copy by id. The window keeps its length and
          // both ends, so the reader keeps their place and nothing counts as
          // new. (A locked row has no ciphertext left to open again, D137.)
          const newestHeld = shown.messages[shown.messages.length - 1]!;
          const fresh = new Map<string, ChatMessage>();
          let response = window;
          let after = shown.messages[0].createdAt;
          for (let pages = 0; response?.success && pages < 20; pages++) {
            const rows = (response.data as ChatMessage[]).reverse();
            rows.forEach((message) => fresh.set(message.id, message));
            const last = rows[rows.length - 1];
            if (
              !response.pagination.truncated ||
              !last ||
              last.createdAt >= newestHeld.createdAt ||
              last.createdAt === after
            ) {
              break;
            }
            after = last.createdAt;
            response = await api
              .getMessagesAfter(conversationId, after)
              .catch(() => null);
            if (cancelled) return;
          }
          if (fresh.size > 0) {
            setPage((previous) =>
              previous.conversationId === conversationId && previous.hasNewer
                ? {
                    ...previous,
                    messages: previous.messages.map(
                      (message) => fresh.get(message.id) ?? message
                    ),
                  }
                : previous
            );
          }
          return;
        }
        if (window?.success && !window.pagination.truncated) {
          setPage({
            conversationId,
            messages: (window.data as ChatMessage[]).reverse(),
            hasMore: shown.hasMore,
            cursor: shown.cursor,
          });
          return;
        }
      }
      // The first page shows at once (D229).
      newWindow();
      setPage({ conversationId, messages: fresh, hasMore, cursor });
      // The search below extends this window only while it is on screen.
      const gen = windowGenRef.current;
      if (!opening) return;
      const openedAt = fresh[fresh.length - 1]?.createdAt ?? '';
      setUnread({ conversationId, marker: null, openedAt });
      unreadRef.current = { conversationId, marker: null, openedAt };
      if (!userId) return;

      // Everything on the first page is unread: look further back in the
      // background, adding each older page as scrolling up would, and place
      // the divider once the first unread message is found.
      let list = fresh;
      if (hasMore && cursor && firstUnreadMayBeOlder(list, userId)) {
        loadingOlderRef.current = true;
        searching = list;
        setSearchingUnread(true);
        // At most a few seconds, then the divider goes on what's loaded.
        let timer: ReturnType<typeof setTimeout> | undefined;
        const timeout = new Promise<'timeout'>((resolve) => {
          timer = setTimeout(
            () => resolve('timeout'),
            UNREAD_SEARCH_TIMEOUT_MS
          );
        });
        try {
          while (
            hasMore &&
            cursor &&
            list.length < UNREAD_SEARCH_LIMIT &&
            firstUnreadMayBeOlder(list, userId)
          ) {
            const older:
              | Awaited<ReturnType<typeof api.getMessages>>
              | 'timeout'
              | null = await Promise.race([
              api
                .getMessages(conversationId, cursor, UNREAD_PAGE_SIZE)
                .catch(() => null),
              timeout,
            ]);
            if (cancelled) return;
            if (older === 'timeout' || !older?.success) break;
            // A jump replaced the window: it wins (D229), the search stops.
            if (windowGenRef.current !== gen) break;
            const seen = new Set(list.map((message) => message.id));
            const added = (older.data as ChatMessage[])
              .reverse()
              .filter((message) => !seen.has(message.id));
            list = [...added, ...list];
            searching = list;
            const next: { hasMore: boolean; cursor: string | undefined } = {
              hasMore: older.pagination.hasMore,
              cursor: older.pagination.cursor,
            };
            hasMore = next.hasMore;
            cursor = next.cursor;
            setPage((previous) => {
              if (
                previous.conversationId !== conversationId ||
                windowGenRef.current !== gen
              ) {
                return previous;
              }
              const onScreen = new Set(previous.messages.map((m) => m.id));
              return {
                ...previous,
                messages: [
                  ...added.filter((message) => !onScreen.has(message.id)),
                  ...previous.messages,
                ],
                ...next,
              };
            });
          }
        } finally {
          clearTimeout(timer);
          searching = null;
          // A search cancelled by a chat switch must not free the ref the
          // new chat's search is holding.
          if (!cancelled) {
            loadingOlderRef.current = false;
            setSearchingUnread(false);
          }
        }
      }
      if (cancelled) return;
      // Jumped elsewhere mid-search: no divider to place (D229).
      if (windowGenRef.current !== gen) return;
      const marker = unreadMarker(list, userId);
      setUnread({ conversationId, marker, openedAt });
    };
    load().catch((error: unknown) => {
      if (!cancelled) setFirstPage({ conversationId, loading: false, error });
    });
    subscribe(conversationId);

    return () => {
      cancelled = true;
      if (!searching) return;
      // Left mid-search: the mark-read effect never saw these, so mark the
      // chat read up to the newest one here, for other devices' unread counts
      // and badges.
      setSearchingUnread(false);
      const toMark = searching.filter(
        (message) =>
          message.sender?.id !== userId && !markedRef.current.has(message.id)
      );
      searching = null;
      if (toMark.length === 0) return;
      toMark.forEach((message) => markedRef.current.add(message.id));
      api
        .markMessagesRead(
          conversationId,
          toMark.map((message) => message.id)
        )
        .then(() => onMarkedReadRef.current(conversationId))
        .catch(() => {
          toMark.forEach((message) => markedRef.current.delete(message.id));
        });
    };
  }, [conversationId, subscribe, reloadToken, userId, newWindow]);

  // The divider goes once I send something here (D229).
  const marker =
    unread?.conversationId === conversationId ? unread.marker : null;
  useEffect(() => {
    if (!marker || !unread || !userId) return;
    const sentSince = messages.some(
      (message) =>
        message.sender?.id === userId && message.createdAt > unread.openedAt
    );
    if (sentSince) setUnread({ ...unread, marker: null });
  }, [messages, marker, unread, userId]);

  // Catch up after the socket reconnects or the app wakes (issue #75):
  // messages sent in between never arrived live, so fetch the newest page and
  // merge it into what's shown. On a wake the socket may still be dead, so
  // this doesn't wait for it; and an iPhone's first request after a freeze
  // can fail while the network comes back, so a failure tries once more.
  // The pending retry: this chat's only, dropped on a switch or unmount.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // The chat on screen now (null once unmounted): a request that fails after
  // a switch doesn't schedule a retry for the chat that was left.
  const openChatRef = useRef(conversationId);
  const catchUp = useCallback(
    function catchUpNow(retry = false) {
      if (!conversationId) return;
      // Reading far back (D271): the newest page would leave a gap; "Jump to
      // latest" loads it.
      if (pageRef.current.hasNewer) return;
      const again = () => {
        if (!retry || openChatRef.current !== conversationId) return;
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = undefined;
          if (openChatRef.current === conversationId) catchUpNow(false);
        }, CATCH_UP_RETRY_MS);
      };
      api
        .getMessages(conversationId)
        .then((response) => {
          if (!response.success) return again();
          const fresh = (response.data as ChatMessage[]).reverse();
          setPage((previous) =>
            previous.conversationId === conversationId && !previous.hasNewer
              ? { ...previous, messages: mergeNewest(previous.messages, fresh) }
              : previous
          );
        })
        .catch(again);
    },
    [conversationId]
  );
  useEffect(() => {
    openChatRef.current = conversationId;
    return () => {
      openChatRef.current = null;
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = undefined;
    };
  }, [conversationId]);

  const seenEpochRef = useRef(connectionEpoch);
  useEffect(() => {
    if (connectionEpoch === seenEpochRef.current) return;
    const firstConnect = seenEpochRef.current === 0;
    seenEpochRef.current = connectionEpoch;
    // The first connection comes with the initial load; later ones are
    // reconnects.
    if (!firstConnect) catchUp();
  }, [connectionEpoch, catchUp]);

  // Back to the front, back from the bfcache, focus, online, or a tapped
  // notification (useWebSocket's wake). A notification for another chat
  // opens that one, which loads it anyway.
  useEffect(
    () =>
      onWake((wake) => {
        if (
          wake.reason === 'notification' &&
          wake.conversationId &&
          wake.conversationId !== conversationId
        ) {
          return;
        }
        catchUp(true);
      }),
    [onWake, catchUp, conversationId]
  );

  // A ref, not the state, guards re-entry: scroll events fire faster than
  // React re-renders with loadingOlder=true. (Declared above: the unread
  // search holds it too.)
  const loadOlderFrom = useCallback(
    async (cursor: string) => {
      if (!conversationId || loadingOlderRef.current) return;
      const gen = windowGenRef.current;
      loadingOlderRef.current = true;
      setLoadingOlder(true);
      setOlderError(null);
      try {
        const response = await api.getMessages(conversationId, cursor);
        if (!response.success) {
          throw new Error('Could not load older messages');
        }
        // Another window came meanwhile: this page isn't its history.
        if (windowGenRef.current !== gen) return;
        const older = (response.data as ChatMessage[]).reverse();
        setPage((previous) => {
          if (previous.conversationId !== conversationId) return previous;
          const seen = new Set(previous.messages.map((message) => message.id));
          return {
            // Keeps hasNewer: a far-back window stays short of the newest.
            ...previous,
            messages: [
              ...older.filter((message) => !seen.has(message.id)),
              ...previous.messages,
            ],
            hasMore: response.pagination.hasMore,
            cursor: response.pagination.cursor,
          };
        });
      } catch (error) {
        // Try again at the top (D270): the sentinel stays quiet until then,
        // so a dead connection isn't asked again and again.
        if (windowGenRef.current === gen) {
          setOlderError({ conversationId, error });
        }
      } finally {
        loadingOlderRef.current = false;
        setLoadingOlder(false);
      }
    },
    [conversationId]
  );
  const loadOlderFromRef = useRef(loadOlderFrom);
  loadOlderFromRef.current = loadOlderFrom;
  const loadOlder = useCallback(async () => {
    if (!current.hasMore || !current.cursor) return;
    await loadOlderFrom(current.cursor);
  }, [current.hasMore, current.cursor, loadOlderFrom]);
  const retryOlder = useCallback(() => {
    setOlderError(null);
    void loadOlder();
  }, [loadOlder]);

  // Merge messages received over the WebSocket (they land in store/chat).
  useEffect(() => {
    if (!conversationId) return;
    const incoming = wsMessages[conversationId];
    if (!incoming || incoming.length === 0) return;

    setPage((previous) => {
      if (previous.conversationId !== conversationId) return previous;
      // Reading far back (D271): newer messages come with Jump to latest.
      if (previous.hasNewer) return previous;
      const seen = new Set(previous.messages.map((message) => message.id));
      // The live store keeps everything since the socket connected; anything
      // older than the loaded window belongs to history paging, not here.
      const oldest = previous.messages[0]?.createdAt ?? '';
      const additions = (incoming as unknown as ChatMessage[]).filter(
        (message) => {
          if (seen.has(message.id) || message.createdAt < oldest) return false;
          if (isExpired(message.expiresAt)) return false;
          seen.add(message.id); // also dedupes within this batch
          return true;
        }
      );
      if (additions.length === 0) return previous;
      const merged = [...previous.messages, ...additions].sort((a, b) =>
        a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0
      );
      return { ...previous, messages: merged };
    });
    // page.conversationId: re-merge once the first page lands, picking up
    // anything that arrived while it was in flight. page.hasNewer: likewise
    // once a far-back window gives way to the newest (D271).
  }, [wsMessages, conversationId, page.conversationId, page.hasNewer]);

  // Mark messages read while the conversation is open (M17 unread badges).
  // Ids are remembered so re-renders and WS arrivals don't re-POST them.
  // Not while the unread search runs: it marks everything once it's done.
  useEffect(() => {
    if (!conversationId || !currentUser || searchingUnread) return;

    const unread = messages.filter(
      (message) =>
        message.sender?.id !== currentUser.id &&
        !markedRef.current.has(message.id)
    );
    if (unread.length === 0) return;

    unread.forEach((message) => markedRef.current.add(message.id));

    api
      .markMessagesRead(
        conversationId,
        unread.map((message) => message.id)
      )
      .then(() => onMarkedRead(conversationId))
      .catch(() => {
        unread.forEach((message) => markedRef.current.delete(message.id));
      });
  }, [messages, conversationId, currentUser, onMarkedRead, searchingUnread]);

  const ownMessage = useCallback(
    (data: SentMessage): ChatMessage => ({
      sender: currentUser
        ? {
            id: currentUser.id,
            handle: currentUser.handle,
            displayName: currentUser.displayName,
            avatarUrl: currentUser.avatarUrl,
          }
        : null,
      readBy: [],
      reactions: [],
      replyTo: null,
      editedAt: null,
      deletedAt: null,
      ...data,
    }),
    [currentUser]
  );

  const appendOwnMessage = useCallback(
    (data: SentMessage) => {
      const shown = pageRef.current;
      if (shown.hasNewer && shown.conversationId === data.conversationId) {
        // Sent while reading far back (D271): the message shows at once, as
        // a new window at the newest end, and the history above it loads as
        // older pages do (the sentinel, Try again on failure). The ref is
        // set too, so a second send before the render appends to it.
        const next: MessagePage = {
          conversationId: data.conversationId,
          messages: [ownMessage(data)],
          hasMore: true,
          cursor: data.createdAt,
        };
        newWindow();
        pageRef.current = next;
        setPage(next);
        void loadOlderFromRef.current(data.createdAt);
        return;
      }
      setPage((previous) => {
        if (previous.conversationId !== data.conversationId) return previous;
        if (previous.messages.some((message) => message.id === data.id)) {
          return previous;
        }
        return {
          ...previous,
          messages: [...previous.messages, ownMessage(data)],
        };
      });
    },
    [ownMessage, newWindow]
  );

  // Live edits, deletes, reactions and read receipts (M20 §5).
  useEffect(() => {
    if (!conversationId) return;
    return onEvent(({ event, payload }) => {
      const data = payload as { conversationId?: string };
      if (data.conversationId !== conversationId) return;

      if (event === WS_EVENTS.MESSAGE_UPDATED) {
        const updated = payload as ChatMessage;
        setMessages((list) =>
          list.map((message) =>
            message.id === updated.id
              ? // Stars are personal; a broadcast doesn't carry mine.
                {
                  ...updated,
                  starredByMe: updated.deletedAt ? false : message.starredByMe,
                }
              : message.replyTo?.id === updated.id
                ? {
                    ...message,
                    replyTo: {
                      ...message.replyTo,
                      deleted: Boolean(updated.deletedAt),
                      preview: updated.deletedAt ? '' : message.replyTo.preview,
                    },
                  }
                : message
          )
        );
      } else if (event === WS_EVENTS.MESSAGE_REACTION) {
        const { messageId, userId, emoji } = payload as {
          messageId: string;
          userId: string;
          emoji: string | null;
        };
        setMessages((list) =>
          list.map((message) => {
            if (message.id !== messageId) return message;
            const others = (message.reactions ?? []).filter(
              (reaction) => reaction.userId !== userId
            );
            return {
              ...message,
              reactions: emoji ? [...others, { userId, emoji }] : others,
            };
          })
        );
      } else if (event === WS_EVENTS.MESSAGE_DELETED) {
        // Disappeared for everyone (D238): gone, and quotes of them too.
        const gone = new Set((payload as WsMessageDeletedPayload).messageIds);
        setMessages((list) =>
          list
            .filter((message) => !gone.has(message.id))
            .map((message) =>
              message.replyTo && gone.has(message.replyTo.id)
                ? { ...message, replyTo: null }
                : message
            )
        );
      } else if (event === WS_EVENTS.MESSAGE_POLL) {
        // New counts after a vote or close (M60, D220).
        setMessages((list) => applyPollEvent(list, payload as WsPollPayload));
      } else if (event === WS_EVENTS.MESSAGE_GATHERING) {
        // Replies, arrivals, rings spent or a cancel (M71, D282).
        setMessages((list) =>
          applyGatheringEvent(list, payload as WsGatheringPayload)
        );
      } else if (event === WS_EVENTS.MESSAGE_READ) {
        // Read watermarks (D122): `readUpTo` covers messages past the ids.
        const read = payload as {
          userId: string;
          messageIds: string[];
          readUpTo?: string;
        };
        setMessages((list) => applyRead(list, read));
      }
    });
  }, [conversationId, onEvent, setMessages]);

  // Drop a message from view (delete for me, D30).
  const removeMessage = useCallback(
    (messageId: string) =>
      setMessages((list) => list.filter((message) => message.id !== messageId)),
    [setMessages]
  );

  // Replace one message with a fresh server copy (own edit/delete/reaction).
  const replaceMessage = useCallback(
    (updated: ChatMessage) =>
      setMessages((list) =>
        list.map((message) =>
          message.id === updated.id ? { ...message, ...updated } : message
        )
      ),
    [setMessages]
  );

  // Show a search hit (M20 §6): load from that message to the newest. Returns
  // false when it is too far back to load in one window.
  // A window around a pinned, found or starred message is on its way: the
  // list stays, with the small mark at the top (M66, D250).
  const [jumping, setJumping] = useState(false);
  const jumpTo = useCallback(
    async (messageId: string, createdAt: string): Promise<boolean> => {
      if (!conversationId) return false;
      if (current.messages.some((message) => message.id === messageId)) {
        return true;
      }
      setJumping(true);
      try {
        const response = await api.getMessagesAfter(conversationId, createdAt);
        if (!response.success) return false;
        // Too far back for one window: show the window from the message on,
        // short of the newest; "Jump to latest" brings the rest (D271).
        const truncated = response.pagination.truncated;
        newWindow();
        setPage({
          conversationId,
          messages: (response.data as ChatMessage[]).reverse(),
          hasMore: response.pagination.hasMore,
          cursor: response.pagination.cursor,
          ...(truncated && {
            hasNewer: true,
            newerBaseline: new Set(
              (wsMessagesRef.current[conversationId] ?? []).map(
                (message) => message.id
              )
            ),
          }),
        });
        return true;
      } finally {
        setJumping(false);
      }
    },
    [conversationId, current.messages, newWindow]
  );

  // Jump to latest (D271) after a jump far back: the newest page replaces
  // the window, as when the chat opened.
  // One request at a time per chat; an answer for a window that has since
  // been replaced (a jump, a send) is dropped.
  const [loadingLatest, setLoadingLatest] = useState<string | null>(null);
  const latestRef = useRef<{
    conversationId: string;
    promise: Promise<boolean>;
  } | null>(null);
  const loadLatest = useCallback((): Promise<boolean> => {
    if (!conversationId) return Promise.resolve(false);
    if (latestRef.current?.conversationId === conversationId) {
      return latestRef.current.promise;
    }
    const gen = windowGenRef.current;
    const request = { conversationId } as {
      conversationId: string;
      promise: Promise<boolean>;
    };
    setLoadingLatest(conversationId);
    request.promise = (async () => {
      try {
        const response = await api.getMessages(conversationId);
        if (!response.success || windowGenRef.current !== gen) return false;
        const fresh = (response.data as ChatMessage[]).reverse();
        newWindow();
        setPage((previous) =>
          previous.conversationId === conversationId
            ? {
                conversationId,
                messages: fresh,
                hasMore: response.pagination.hasMore,
                cursor: response.pagination.cursor,
              }
            : previous
        );
        return true;
      } catch {
        return false;
      } finally {
        if (latestRef.current === request) latestRef.current = null;
        setLoadingLatest((loading) =>
          loading === conversationId ? null : loading
        );
      }
    })();
    latestRef.current = request;
    return request.promise;
  }, [conversationId, newWindow]);

  // Messages from others that arrived while the window is short of the
  // newest: the button's badge (D271).
  const newerCount = useMemo(() => {
    if (!conversationId || !current.hasNewer) return 0;
    const baseline = current.newerBaseline;
    // As the chat list counts: not D234 group-info lines, nor what has
    // already disappeared (D238).
    return (wsMessages[conversationId] ?? []).filter((message) => {
      const live = message as unknown as ChatMessage;
      return (
        !baseline?.has(live.id) &&
        live.sender?.id !== userId &&
        !isGroupInfoLine(live) &&
        !isExpired(live.expiresAt)
      );
    }).length;
  }, [
    conversationId,
    current.hasNewer,
    current.newerBaseline,
    wsMessages,
    userId,
  ]);

  return {
    messages,
    setMessages,
    replaceMessage,
    removeMessage,
    jumpTo,
    hasMore: current.hasMore,
    /** The first page is on its way and nothing is shown yet (M66). */
    loading: firstPage.conversationId === conversationId && firstPage.loading,
    /** The first page failed; `reload` tries again (D249). */
    loadError:
      firstPage.conversationId === conversationId ? firstPage.error : null,
    /** Older pages are being fetched behind the first unread (D229). */
    searchingUnread,
    jumping,
    loadingOlder,
    loadOlder,
    /** An older page failed; `retryOlder` tries again (D270). */
    olderError:
      olderError?.conversationId === conversationId ? olderError.error : null,
    retryOlder,
    /** The window stops short of the newest message (D271). */
    hasNewer: Boolean(current.hasNewer),
    /** Messages from others that arrived meanwhile, while `hasNewer`. */
    newerCount,
    loadingLatest: loadingLatest !== null && loadingLatest === conversationId,
    loadLatest,
    appendOwnMessage,
    reload,
    /** Where the unread divider goes, while it shows (D229). */
    unreadMarker: marker,
  };
}
